import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { yupResolver } from "@hookform/resolvers/yup"
import { SubmitHandler, useForm } from "react-hook-form"
import notify from "@/shared/lib/notify"
import { getErrorMessage } from "@/shared/consts/error-codes"
import {
    closeVisibilityAclModal,
    fetchSetVisibilityAcl,
    fetchGetAclList,
    fetchDeleteAcl,
    visibilityAclValidationSchema,
    selectOnVisibilityAclChange,
    selectCurrentGroup,
    selectCurrentUser,
    selectVisibilityAclSourceType,
} from "../../model"
import { TargetType, AclStrategy, IVisibilityAcl, ISetVisibilityAclRequest } from "@/shared/api/groups"
import { selectUsersGroups, selectEmployeesGroups } from "@/entities/groups"
import { VisibilityAclForm, VisibilityAclList } from "@/entities/visibility-acl"
import { NULL_GROUP_OPTION_VALUE, ACL_STRATEGY_OPTIONS, getAclStrategyLabel } from "@/entities/visibility-acl"
import { useMemo, useEffect, useState, FormEventHandler } from "react"
import { useWatch } from "react-hook-form"
import type { VisibilityAclFormValues } from "@/entities/visibility-acl"

export const VisibilityAclModalContent = () => {
    const dispatch = useAppDispatch()
    const onVisibilityAclChange = useAppSelector(selectOnVisibilityAclChange)
    const currentGroup = useAppSelector(selectCurrentGroup)
    const currentUser = useAppSelector(selectCurrentUser)
    const sourceType = useAppSelector(selectVisibilityAclSourceType)
    const usersGroups = useAppSelector(selectUsersGroups)
    const employeesGroups = useAppSelector(selectEmployeesGroups)

    const [aclList, setAclList] = useState<IVisibilityAcl[]>([])
    const [isLoadingList, setIsLoadingList] = useState(false)
    const [isDeleting, setIsDeleting] = useState<string | null>(null)

    const allGroups = useMemo(() => [...usersGroups, ...employeesGroups], [usersGroups, employeesGroups])

    const visibleGroupsSelectOptions = useMemo(
        () => [
            { value: NULL_GROUP_OPTION_VALUE, label: 'Все группы' },
            ...allGroups.map((group) => ({ value: group.id, label: group.name })),
        ],
        [allGroups]
    )

    const { handleSubmit, control, formState: { errors, isSubmitting }, reset, setValue } = useForm<VisibilityAclFormValues>({
        defaultValues: {
            acl_strategy: AclStrategy.INHERIT_ONLY,
            visible_group_ids: null,
        },
        resolver: yupResolver(visibilityAclValidationSchema) as any,
    })

    const strategy = useWatch({ control, name: 'acl_strategy', defaultValue: AclStrategy.INHERIT_ONLY })

    const targetId = sourceType === TargetType.GROUP ? currentGroup.id : currentUser?.id

    // Group modal: clear visible groups when strategy is inherit-only
    useEffect(() => {
        if (sourceType === TargetType.GROUP && strategy === AclStrategy.INHERIT_ONLY) {
            setValue('visible_group_ids', null)
        }
    }, [sourceType, strategy, setValue])

    useEffect(() => {
        if (targetId) {
            loadAclList()
        }
    }, [sourceType, targetId])

    const loadAclList = async () => {
        if (!sourceType || !targetId) return
        setIsLoadingList(true)
        try {
            const result = await dispatch(fetchGetAclList()).unwrap()
            setAclList(result)
        } catch (error) {
            notify.error(getErrorMessage(error))
        } finally {
            setIsLoadingList(false)
        }
    }

    const handleClose = () => {
        dispatch(closeVisibilityAclModal())
        reset()
        setAclList([])
    }

    const handleDeleteAcl = async (aclId: string) => {
        setIsDeleting(aclId)
        try {
            await dispatch(fetchDeleteAcl(aclId)).unwrap()
            notify.success('ACL успешно удален')
            loadAclList()
            onVisibilityAclChange?.()
        } catch (error) {
            notify.error(getErrorMessage(error))
        } finally {
            setIsDeleting(null)
        }
    }

    const onSubmit: SubmitHandler<VisibilityAclFormValues> = async (data) => {
        if (!sourceType || !targetId) return

        let visibleGroupIds: string[] | null = null
        if (data.visible_group_ids && data.visible_group_ids.length > 0) {
            visibleGroupIds = data.visible_group_ids.filter((id) => id !== NULL_GROUP_OPTION_VALUE)
            if (visibleGroupIds.length === 0) visibleGroupIds = null
        }
        if (data.visible_group_ids?.includes(NULL_GROUP_OPTION_VALUE)) {
            visibleGroupIds = null
        }

        const requestData: ISetVisibilityAclRequest = {
            target_type: sourceType,
            target_id: targetId,
            ...(sourceType === TargetType.GROUP && { acl_strategy: data.acl_strategy }),
            visible_group_ids: sourceType === TargetType.GROUP && strategy === AclStrategy.INHERIT_ONLY ? null : visibleGroupIds,
        }

        try {
            await dispatch(fetchSetVisibilityAcl(requestData)).unwrap()
            notify.success('ACL успешно создан')
            reset({ acl_strategy: AclStrategy.INHERIT_ONLY, visible_group_ids: null })
            loadAclList()
            onVisibilityAclChange?.()
        } catch (error) {
            notify.error(getErrorMessage(error))
        }
    }

    if (!targetId) return null

    const showStrategySelect = sourceType === TargetType.GROUP
    const showVisibleGroupsSelect = sourceType === TargetType.USER || strategy !== AclStrategy.INHERIT_ONLY

    return (
        <div className="flex flex-col gap-4">
            <VisibilityAclForm
                control={control}
                errors={errors}
                isSubmitting={isSubmitting}
                onSubmit={handleSubmit(onSubmit) as FormEventHandler<HTMLFormElement>}
                onCancel={handleClose}
                visibleGroupsSelectOptions={visibleGroupsSelectOptions}
                showStrategySelect={showStrategySelect}
                showVisibleGroupsSelect={showVisibleGroupsSelect}
            />
            <VisibilityAclList
                aclList={aclList}
                isLoading={isLoadingList}
                isDeleting={isDeleting}
                onDelete={handleDeleteAcl}
                getStrategyLabel={(strategy) => getAclStrategyLabel(strategy, ACL_STRATEGY_OPTIONS)}
            />
        </div>
    )
}
